import React from 'react'
import { Shield, Clock, CreditCard, UserX, CheckCircle, Headphones } from 'lucide-react'

export default function RiskReversal() {
  const guarantees = [
    {
      icon: CreditCard,
      title: 'Simple Monthly Pricing',
      description: 'All features included for just P***/month',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Shield,
      title: '30-Day Money-Back',
      description: 'Not satisfied? Get a full refund, no questions asked',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: UserX,
      title: 'Cancel Anytime',
      description: 'No long-term contracts or cancellation fees',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: CheckCircle,
      title: 'No Hidden Fees',
      description: 'One plan, one price, no surprise charges',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Clock,
      title: 'Setup in Under 1 Hour',
      description: 'Easy onboarding with step-by-step guidance',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: Headphones,
      title: 'Training & Support Included',
      description: 'Our team helps you get up and running fast',
      color: 'from-teal-500 to-green-500'
    }
  ]

  return (
    <section className="py-20 bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-full mb-4">
            <span className="text-green-700 dark:text-green-400 font-semibold text-sm">
              🛡️ 100% RISK-FREE
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Zero Risk.
            <span className="gradient-text"> Pure Gain.</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            We're so confident you'll love LaundryMate, we've removed all the risk
          </p>
        </div>

        {/* Guarantees Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {guarantees.map((guarantee, index) => (
            <div
              key={index}
              className="group bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 relative overflow-hidden border border-gray-100 dark:border-gray-700"
            >
              {/* Gradient background on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${guarantee.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />

              {/* Icon */}
              <div className={`w-14 h-14 bg-gradient-to-br ${guarantee.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <guarantee.icon className="w-7 h-7 text-white" />
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                {guarantee.title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {guarantee.description}
              </p>
            </div>
          ))}
        </div>

        {/* Big Guarantee Badge */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24" />

            <div className="relative z-10 text-center text-white">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-3xl sm:text-4xl font-bold mb-4">
                Our Promise to You
              </h3>
              <p className="text-lg sm:text-xl text-white/90 mb-6 max-w-2xl mx-auto">
                If LaundryMate doesn't help you save time, increase revenue, and make your life easier within 30 days - we'll give you a <strong>full refund</strong>. No questions asked.
              </p>
              <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full border border-white/30">
                <CheckCircle className="w-6 h-6" />
                <span className="font-semibold text-lg">100% Money-Back Guarantee</span>
              </div>
            </div>
          </div>
        </div>

        {/* What You Get List */}
        <div className="mt-16 max-w-3xl mx-auto bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 rounded-2xl p-8 border border-cyan-200 dark:border-cyan-800">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            When You Get Started, You Get:
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              'Full access to all features',
              '1-on-1 onboarding call',
              'Step-by-step setup guide',
              'Email & chat support',
              'Video tutorials library',
              'Cancel anytime, no penalties'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-700 dark:text-gray-300 font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 dark:text-gray-400 mb-4 text-lg">
            Ready to start LaundryMate risk-free?
          </p>
          <a
            href="https://app.laundrymate.shop"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
          >
            Get Started Now
          </a>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-3">
            30-day money-back guarantee • Cancel anytime
          </p>
        </div>
      </div>
    </section>
  )
}
